import { VISITOR_COUNT_API_URL } from "../utils/constants";
import axios from "axios";

const VISITOR_COUNT_STORAGE_KEY = "nmdfc-visitor-count";
const VISITOR_COUNT_REQUESTED_STORAGE_KEY = "nmdfc-visitor-count-requested";

const getStoredItem = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredItem = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Continue with the in-memory cache if browser storage is unavailable.
  }
};

const removeStoredItem = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clean up if browser storage is unavailable.
  }
};

const getStoredVisitorCount = () => {
  const storedCount = getStoredItem(VISITOR_COUNT_STORAGE_KEY);
  const numericCount = Number(storedCount);

  return storedCount !== null && Number.isFinite(numericCount)
    ? numericCount
    : undefined;
};

let cachedVisitorCount = getStoredVisitorCount();
let visitorCountRequest;

const getNumericValue = (value) => {
  if (typeof value !== "number" && typeof value !== "string") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * Fetches the total visitor count from the visitor service.
 * Supports the common `{ count }`, `{ data: { count } }`, and numeric response shapes.
 */
export const getVisitorCount = () => {
  if (cachedVisitorCount !== undefined) {
    return Promise.resolve(cachedVisitorCount);
  }

  if (visitorCountRequest) {
    return visitorCountRequest;
  }

  // Mark this before the request. Bhashini can reload the page while the
  // request is pending; without this persistent marker, that reload sends a
  // second count.
  if (getStoredItem(VISITOR_COUNT_REQUESTED_STORAGE_KEY) === "true") {
    return Promise.resolve(null);
  }

  setStoredItem(VISITOR_COUNT_REQUESTED_STORAGE_KEY, "true");

  visitorCountRequest = axios.get(VISITOR_COUNT_API_URL).then((response) => {
    const data = response.data;

    const count = [
      data?.count,
      data?.visitorCount,
      data?.visitor_count,
      data?.data?.count,
      data?.data?.visitorCount,
      data?.data?.totalCount,
      data?.data?.visitor_count,
      data?.data,
      data,
    ]
      .map(getNumericValue)
      .find((value) => value !== null);

    if (count === undefined) {
      throw new Error("Visitor count API returned an invalid count.");
    }

    cachedVisitorCount = count;
    setStoredItem(VISITOR_COUNT_STORAGE_KEY, String(count));

    return count;
  });

  return visitorCountRequest.catch((error) => {
    // Let a later mount retry if the initial request failed.
    visitorCountRequest = undefined;
    removeStoredItem(VISITOR_COUNT_REQUESTED_STORAGE_KEY);
    throw error;
  });
};

